// Lógica PURA del importador de planillas de la Economía del cliente (contrato §3.5).

// Las columnas que el backend reconoce (tolera acentos y mayúsculas). Son nombres de COLUMNA de un
// archivo, no copy de pantalla: van tal cual, en el orden del contrato.
export const IMPORT_COLUMNS = [
    'cliente',
    'margen_bruto_pct',
    'ticket_promedio',
    'envio_por_orden',
    'pasarela_impuestos_pct',
    'costos_fijos_mes',
    'honorarios_mes',
    'moneda',
    'vigente_desde',
    'nota',
];

// Un archivo de más de 1 MB lo rechaza el backend (§3.5): se corta antes de subirlo.
export const IMPORT_MAX_BYTES = 1024 * 1024;

// Los casos que la vista previa nombra fila por fila. `ERROR_CASES` son los que no se aplican.
export const IMPORT_CASES = ['CREATE', 'UPDATE', 'UNCHANGED', 'CLIENT_NOT_FOUND', 'AMBIGUOUS_CLIENT',
    'INVALID_NUMBER', 'DUPLICATE_ROW', 'CURRENCY_MISMATCH'];
// Avisos de una fila que se aplica igual (`row.warnings`).
export const IMPORT_WARNINGS = new Set(['LATER_VERSION', 'PERCENT_LOOKS_LIKE_FRACTION']);
export const ERROR_CASES = new Set(['CLIENT_NOT_FOUND', 'AMBIGUOUS_CLIENT', 'INVALID_NUMBER',
    'DUPLICATE_ROW', 'CURRENCY_MISMATCH']);

// Errores con los que el backend pide volver a subir la planilla: el `token` firmado de la vista
// previa venció (15 min) o es inválido (`IMPORT_PREVIEW_EXPIRED`), o el archivo que llega a aplicar
// no es el de la vista previa (`IMPORT_FILE_MISMATCH`). La salida es la misma para los dos.
const EXPIRED_CODES = new Set(['IMPORT_PREVIEW_EXPIRED', 'IMPORT_FILE_MISMATCH']);

/**
 * La plantilla vacía: sólo el encabezado. Sin fila de ejemplo a propósito — una fila con un cliente
 * inventado que alguien olvida borrar vuelve en la vista previa como `CLIENT_NOT_FOUND`.
 */
export function importTemplateCsv() {
    return `${IMPORT_COLUMNS.join(',')}\n`;
}

/** Cuántos cambios aplica el botón: creaciones + actualizaciones (lo sin cambios no escribe nada). */
export function applicableCount(summary) {
    if (!summary) return 0;
    return (Number(summary.create) || 0) + (Number(summary.update) || 0);
}

export function hasErrors(summary) {
    return !!summary && (Number(summary.errors) || 0) > 0;
}

/** ¿Se puede aplicar? Hay algo para escribir, y si hay errores el usuario pidió saltearlos. */
export function canApply(summary, skipErrors) {
    return applicableCount(summary) > 0 && (!hasErrors(summary) || skipErrors === true);
}

export function isErrorCase(caseCode) {
    return ERROR_CASES.has(caseCode);
}

/** ¿La respuesta dice que la vista previa venció? */
export function isExpiredResponse(response) {
    if (!response) return false;
    const body = response.raw_body || {};
    // El código puede venir en cualquiera de los tres lugares según quién armó la respuesta (el
    // backend en el body, `normalizeMultipartResponse` en `error_code`, un código tipado en `status`).
    return [response.error_code, response.status, body.error_code]
        .some((code) => EXPIRED_CODES.has(String(code || '')));
}

// Rechazos del archivo entero con salida conocida (R-ecofe-09). Lo que no esté acá va al genérico:
// nunca el `error_message` del backend.
const REJECTION_KEYS = {
    IMPORT_MISSING_COLUMNS: 'economics.import.error.missing_columns',
    IMPORT_FILE_NOT_CSV: 'economics.import.error.not_csv',
    IMPORT_FILE_TOO_LARGE: 'economics.import.error.too_large',
    IMPORT_HAS_ERRORS: 'economics.import.error.has_errors',
};

function errorCodeOf(response) {
    const body = (response && response.raw_body) || {};
    return [body.error_code, response && response.error_code, response && response.status]
        .find((code) => code && code !== 'ERROR') || null;
}

/**
 * El mensaje de un rechazo del importador: `{key, params}` para `t()`. Con `IMPORT_MISSING_COLUMNS`
 * nombra las columnas que faltan (vienen en `missing`, tal como las espera el backend).
 */
export function importRejection(response) {
    const code = errorCodeOf(response);
    const key = REJECTION_KEYS[code] || 'economics.error.generic';
    if (code !== 'IMPORT_MISSING_COLUMNS') return {key, params: {}};
    const missing = ((response && response.raw_body && response.raw_body.missing) || []).filter(Boolean);
    if (!missing.length) return {key: 'economics.error.generic', params: {}};
    return {key, params: {columns: missing.join(', '), interpolation: {escapeValue: false}}};
}

/**
 * La columna que nombra `INVALID_NUMBER` («Número inválido en {{column}}»): el backend la manda en
 * `row.column` tal como vino en el encabezado del CSV; la del diff queda de red.
 */
export function invalidColumnOf(row) {
    if (!row) return '';
    if (row.column) return String(row.column);
    const diffKeys = Object.keys(row.diff || {});
    return diffKeys.length ? diffKeys[0] : '';
}
